"use client";

import {
	faArrowLeftLong,
	faArrowRightLong,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setUserInteractionDataAttribute } from "@shared/constants";
import Link from "next/link";
import type { MouseEvent } from "react";

import { buildPaginationPageItems } from "@ui/js/components/pagination/buildPageItems";

interface Props {
	currentPage: number;
	pages: number;
	getPageHref: (page: number) => string;
	onPageChange?: (page: number) => void;
	disabled?: boolean;
}

export default function PaginationMenuContent({
	currentPage,
	pages,
	getPageHref,
	onPageChange,
	disabled = false,
}: Props) {
	if (pages < 2) {
		return null;
	}

	const pageItems = buildPaginationPageItems(currentPage, pages);

	function handleNavigate(event: MouseEvent<HTMLAnchorElement>, page: number) {
		if (!onPageChange) {
			return;
		}

		if (
			event.defaultPrevented ||
			event.metaKey ||
			event.ctrlKey ||
			event.shiftKey ||
			event.altKey ||
			event.button !== 0
		) {
			return;
		}

		event.preventDefault();

		if (disabled) {
			return;
		}

		onPageChange(page);
	}

	function renderPageLink(page: number) {
		const isActive = currentPage === page;
		const isPrevious = page < currentPage;
		const className = `pagination__menu__link${
			isActive ? " pagination__menu__link--is-active" : ""
		}${isPrevious ? " pagination__menu__link--is-previous" : ""}`;

		if (isActive) {
			if (onPageChange) {
				return (
					<span
						className={className}
						aria-current="page"
						aria-label={`Page ${page}`}
					>
						{page}
					</span>
				);
			}

			return (
				<Link
					{...setUserInteractionDataAttribute(
						`pagination_menu_${page}_active-page_link`,
					)}
					href="#"
					className={className}
					aria-current="page"
					aria-label={`Page ${page}`}
				>
					{page}
				</Link>
			);
		}

		return (
			<Link
				{...setUserInteractionDataAttribute(`pagination_menu_${page}_link`)}
				href={getPageHref(page)}
				className={className}
				aria-label={`Go to page ${page}`}
				aria-disabled={disabled || undefined}
				onClick={(event) => handleNavigate(event, page)}
			>
				{page}
			</Link>
		);
	}

	function renderArrow(direction: "prev" | "next") {
		const isPrev = direction === "prev";
		const targetPage = isPrev ? currentPage - 1 : currentPage + 1;
		const isDisabled =
			disabled || (isPrev ? currentPage <= 1 : currentPage >= pages);
		const label = isPrev ? "Previous page" : "Next page";
		const icon = isPrev ? faArrowLeftLong : faArrowRightLong;
		const trackingKey = isPrev
			? "pagination_menu_previous_link"
			: "pagination_menu_next_link";

		if (isDisabled) {
			return (
				<span
					className="pagination__menu__link pagination__menu__link--is-hidden"
					aria-hidden="true"
				>
					<FontAwesomeIcon icon={icon} />
				</span>
			);
		}

		return (
			<Link
				{...setUserInteractionDataAttribute(trackingKey)}
				href={getPageHref(targetPage)}
				className="pagination__menu__link"
				aria-label={label}
				aria-disabled={disabled || undefined}
				onClick={(event) => handleNavigate(event, targetPage)}
			>
				<FontAwesomeIcon icon={icon} />
			</Link>
		);
	}

	return (
		<ul className="pagination__menu" data-spec-component="pagination-menu">
			<li className="pagination__menu__item">{renderArrow("prev")}</li>
			{pageItems.map((item) => (
				<li
					className="pagination__menu__item"
					key={item.type === "page" ? `page-${item.page}` : item.key}
				>
					{item.type === "page" ? (
						renderPageLink(item.page)
					) : (
						<span className="pagination__menu__ellipsis" aria-hidden="true">
							...
						</span>
					)}
				</li>
			))}
			<li className="pagination__menu__item">{renderArrow("next")}</li>
		</ul>
	);
}
